import React, { useState, useMemo, useCallback, useEffect } from 'react';
import api from '../services/api';

// ── Design tokens (match FinanceReport/AdmissionReport palette) ──────────────
const navy   = '#1B3B5F';
const deep   = '#0D1F2D';
const green  = '#2D5F4F';
const gold   = '#D4AF37';
const coral  = '#E85D4A';
const orange = '#F59E0B';
const light  = '#E8E4DF';
const cream  = '#F8F6F3';
const sub    = '#6B6B6B';
const white  = '#FFFFFF';

const TYPE_COLOR = {
  'Single (Private)':    '#7C5BA6',
  'Premium (2 Sharing)': '#D4AF37',
  'Twin (2 Sharing)':    '#E07BA8',
  '3 Sharing':           '#C9A227',
  '4 Sharing':           '#5B8DB8',
  '3sharing':            '#C9A227',
  '4sharing':            '#5B8DB8',
  '2sharing':            '#E07BA8',
};
const TYPE_ORDER = ['Single (Private)','Premium (2 Sharing)','Twin (2 Sharing)','3 Sharing','4 Sharing'];
const TYPE_SHORT = {
  'Single (Private)':'Single','Premium (2 Sharing)':'Premium',
  'Twin (2 Sharing)':'Twin','3 Sharing':'3-Share','4 Sharing':'4-Share',
  '3sharing':'3-Share','4sharing':'4-Share','2sharing':'Twin',
};
const TOWER_COLOR = { A: navy, B: green };

const pct  = (a,t) => t ? Math.round(a/t*100) : 0;
const fmt  = n => Number(n||0).toLocaleString('en-IN');

function occColor(p){
  if(p>=85) return navy;
  if(p>=65) return '#3a6d97';
  if(p>=40) return '#6f9bc0';
  if(p>0)   return '#a9c4dc';
  return '#cfdbe6';
}

const PG = 15;

// ── Sub-components ────────────────────────────────────────────────────────────
function SumCard({ label, value, sub: s, accent }) {
  return (
    <div style={{ background: white, borderRadius: 10, padding: '20px 22px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: accent }}/>
      <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: sub, marginBottom: 8, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 30, fontWeight: 600, color: accent === gold ? '#8B6914' : accent, lineHeight: 1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: sub }}>{s}</div>
    </div>
  );
}

function HBar({ label, a, t, color, swatch }) {
  const p = pct(a, t);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
      <div style={{ width: 160, fontSize: 12, color: sub, fontWeight: 500, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        {swatch && <span style={{ width: 11, height: 11, borderRadius: 3, background: color, flexShrink: 0, border: '1px solid rgba(0,0,0,0.12)', display: 'inline-block' }}/>}
        {label}
      </div>
      <div style={{ flex: 1, height: 8, background: light, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: color, borderRadius: 4 }}/>
      </div>
      <div style={{ width: 38, fontSize: 12, fontWeight: 700, textAlign: 'right', flexShrink: 0, color }}>{p}%</div>
      <div style={{ width: 80, fontSize: 11, color: sub, textAlign: 'right', flexShrink: 0 }}>{a} / {t}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const alloc = status === 'Allocated' || status === 'ALLOCATED';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 20,
      fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase',
      background: alloc ? 'rgba(27,59,95,0.1)' : 'rgba(45,95,79,0.12)',
      color: alloc ? navy : green }}>
      {alloc ? 'Allocated' : 'Vacant'}
    </span>
  );
}

function TypePill({ type }) {
  const color = TYPE_COLOR[type] || '#888';
  const label = TYPE_SHORT[type] || type;
  return (
    <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 4,
      fontSize: 10.5, fontWeight: 700, background: color + '33', color: '#5a4a00' }}>
      {label}
    </span>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function BedAllocationReport() {
  const [tab,     setTab]     = useState('overview');
  const [beds,    setBeds]    = useState([]);
  const [loading, setLoading] = useState(true);

  // Register filters
  const [fTower,  setFTower]  = useState('');
  const [fFloor,  setFFloor]  = useState('');
  const [fWing,   setFWing]   = useState('');
  const [fType,   setFType]   = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fQ,      setFQ]      = useState('');
  const [page,    setPage]    = useState(1);

  // Type tab filter
  const [typeTower, setTypeTower] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get('/reports/beds')
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : [];
        setBeds(data);
      })
      .catch(() => setBeds([]))
      .finally(() => setLoading(false));
  }, []);

  // ── Derived stats ──────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const alloc = beds.filter(b => b.status === 'Allocated' || b.status === 'ALLOCATED');
    const A = beds.filter(b => b.tower === 'A');
    const B = beds.filter(b => b.tower === 'B');
    const aA = A.filter(b => b.status === 'Allocated' || b.status === 'ALLOCATED').length;
    const aB = B.filter(b => b.status === 'Allocated' || b.status === 'ALLOCATED').length;
    const floors = [...new Set(beds.map(b => b.floor))].sort((a,b) => a-b);
    const types  = [...new Set(beds.map(b => b.room_type).filter(Boolean))];
    return { total: beds.length, alloc: alloc.length, vac: beds.length - alloc.length,
             A: A.length, B: B.length, aA, aB, floors, types };
  }, [beds]);

  // ── Register filtered ─────────────────────────────────────────────────────
  const regFiltered = useMemo(() => {
    const q = fQ.toLowerCase().trim();
    return beds.filter(b => {
      if (fTower  && b.tower     !== fTower)  return false;
      if (fFloor  && String(b.floor) !== String(fFloor)) return false;
      if (fWing   && b.wing      !== fWing)   return false;
      if (fType   && b.room_type !== fType)   return false;
      if (fStatus && b.status    !== fStatus) return false;
      if (q && !((b.bed_id||'').toLowerCase().includes(q) ||
                 (b.room||'').toLowerCase().includes(q) ||
                 (b.student||b.studentName||'').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [beds, fTower, fFloor, fWing, fType, fStatus, fQ]);

  const pages = Math.max(1, Math.ceil(regFiltered.length / PG));
  const regPage = useMemo(() => {
    const slice = regFiltered.slice((page-1)*PG, page*PG);
    return slice;
  }, [regFiltered, page]);

  // ── Jump to register ───────────────────────────────────────────────────────
  const jumpTo = useCallback((tower, floor, wing) => {
    setTab('register');
    setFTower(tower || '');
    setFFloor(floor ? String(floor) : '');
    setFWing(wing || '');
    setPage(1);
  }, []);

  // ── XLSX export ────────────────────────────────────────────────────────────
  const exportXLSX = useCallback(() => {
    import('xlsx').then(XLSX => {
      const wb = XLSX.utils.book_new();
      const sum = [
        ['Omkara Hostel — Bed Allocation Report'],
        ['Generated', new Date().toLocaleDateString('en-IN')],
        [],
        ['SUMMARY'],
        ['Total Beds', stats.total],
        ['Allocated',  stats.alloc],
        ['Vacant',     stats.vac],
        ['Occupancy %', pct(stats.alloc, stats.total) + '%'],
        [],
        ['By Tower', 'Total', 'Allocated', 'Vacant', 'Occ %'],
        ['Tower A', stats.A, stats.aA, stats.A - stats.aA, pct(stats.aA, stats.A) + '%'],
        ['Tower B', stats.B, stats.aB, stats.B - stats.aB, pct(stats.aB, stats.B) + '%'],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sum), 'Summary');
      const reg = [['Bed ID','Tower','Floor','Wing','Room','Room Type','Status','Occupant']];
      beds.forEach(b => reg.push([b.bed_id, b.tower, b.floor, b.wing, b.room, b.room_type, b.status, b.student || b.studentName || '']));
      const ws = XLSX.utils.aoa_to_sheet(reg);
      ws['!cols'] = [{wch:14},{wch:8},{wch:8},{wch:8},{wch:12},{wch:20},{wch:12},{wch:24}];
      XLSX.utils.book_append_sheet(wb, ws, 'Bed Register');
      XLSX.writeFile(wb, 'Omkara_Bed_Allocation.xlsx');
    });
  }, [beds, stats]);

  // ── PDF print ──────────────────────────────────────────────────────────────
  const printReport = useCallback(() => {
    const style = document.createElement('style');
    style.id = 'bed-print-style';
    style.innerHTML = `@media print {
      .sidebar,[class*="sidebar"],nav,aside,button { display:none!important; }
      .main-content,main { margin-left:0!important; width:100%!important; }
      body { background:white!important; }
      @page { margin:1cm; size:A4 landscape; }
    }`;
    document.head.appendChild(style);
    window.print();
    setTimeout(() => { const el = document.getElementById('bed-print-style'); if(el) el.remove(); }, 1000);
  }, []);

  // ── Styles ─────────────────────────────────────────────────────────────────
  const sel = { padding: '8px 14px', borderRadius: 7, border: `1.5px solid ${light}`,
    fontFamily: 'Work Sans,sans-serif', fontSize: 12.5, color: '#2C2C2C',
    background: white, cursor: 'pointer', outline: 'none' };
  const th0 = { padding: '11px 14px', fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase',
    color: sub, fontWeight: 700, borderBottom: `1px solid ${light}`, background: cream, whiteSpace: 'nowrap' };
  const td0 = { padding: '12px 14px', fontSize: 13, borderBottom: `1px solid ${light}`, verticalAlign: 'middle' };

  const today = new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60, color: sub, fontFamily: 'Work Sans,sans-serif' }}>
      Loading bed data…
    </div>
  );

  return (
    <div style={{ fontFamily: 'Work Sans,sans-serif' }}>

      {/* Action buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 20 }}>
        <button onClick={printReport} style={{ padding: '10px 22px', background: white, color: navy, border: `1.5px solid ${light}`, borderRadius: 6, fontFamily: 'Work Sans,sans-serif', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer' }}>🖨 Print</button>
        <button onClick={exportXLSX} style={{ padding: '10px 22px', background: gold, color: navy, border: 'none', borderRadius: 6, fontFamily: 'Work Sans,sans-serif', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer' }}>⬇ Export XLSX</button>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', background: white, borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 28, overflow: 'hidden' }}>
        {[
          { id:'overview', icon:'🛏', label:'Occupancy Overview', sub:'Tower · Floor · Wing' },
          { id:'types',    icon:'▦', label:'By Room Type',        sub:'Single · Twin · 3 & 4 Sharing' },
          { id:'register', icon:'☷', label:'Bed Register',        sub:'Every bed · Allocated & vacant' },
        ].map(t => (
          <div key={t.id} onClick={() => setTab(t.id)}
            style={{ flex: 1, padding: '16px 20px', textAlign: 'center', cursor: 'pointer',
              borderBottom: `3px solid ${tab === t.id ? gold : 'transparent'}`,
              background: tab === t.id ? 'rgba(212,175,55,0.04)' : white,
              borderRight: `1px solid ${light}`, transition: 'all 0.2s' }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{t.icon}</div>
            <div style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 15, fontWeight: 600, color: tab === t.id ? navy : sub }}>{t.label}</div>
            <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: sub, opacity: 0.7, marginTop: 2 }}>{t.sub}</div>
          </div>
        ))}
      </div>

      {/* ══ OVERVIEW TAB ═══════════════════════════════════════════════════════ */}
      {tab === 'overview' && (
        <div style={{ minHeight: '100vh' }}>
          {/* Header */}
          <div style={{ background: white, borderRadius: 12, padding: '24px 32px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 24, fontWeight: 500, color: navy, marginBottom: 4 }}>Occupancy Overview</h2>
              <p style={{ fontSize: 13, color: sub }}>Both towers · {[...new Set(beds.map(b=>b.floor))].length} floors · {[...new Set(beds.map(b=>b.room))].length} rooms · {stats.total} beds — live status</p>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: navy, background: cream, padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${light}` }}>
              As on {today}
            </div>
          </div>

          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 16, marginBottom: 24 }}>
            <SumCard label="Total Beds"  value={fmt(stats.total)} sub={`${[...new Set(beds.map(b=>b.room))].length} rooms · 2 towers`}  accent={navy}/>
            <SumCard label="Allocated"   value={fmt(stats.alloc)} sub={`${pct(stats.alloc,stats.total)}% occupancy`}                      accent={green}/>
            <SumCard label="Vacant"      value={fmt(stats.vac)}   sub={`${100-pct(stats.alloc,stats.total)}% available`}                  accent={gold}/>
            <SumCard label="Tower A"     value={`${stats.aA} / ${stats.A}`} sub={`${pct(stats.aA,stats.A)}% full`}                       accent={navy}/>
            <SumCard label="Tower B"     value={`${stats.aB} / ${stats.B}`} sub={`${pct(stats.aB,stats.B)}% full`}                       accent={green}/>
          </div>

          {/* Charts row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 22 }}>
            {/* Tower bars */}
            <div style={{ background: white, borderRadius: 12, padding: '24px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 17, fontWeight: 600, color: navy, marginBottom: 6 }}>Occupancy by Tower</div>
              <div style={{ fontSize: 11.5, color: sub, marginBottom: 18 }}>Beds allocated vs total capacity</div>
              <HBar label="Tower A" a={stats.aA} t={stats.A} color={navy}/>
              <HBar label="Tower B" a={stats.aB} t={stats.B} color={green}/>
              {/* Wing split */}
              <div style={{ display: 'flex', gap: 18, marginTop: 16 }}>
                {['Front','Rear'].map(w => {
                  const ws = beds.filter(b => b.wing === w);
                  const wa = ws.filter(b => b.status === 'Allocated' || b.status === 'ALLOCATED').length;
                  return (
                    <div key={w} style={{ flex: 1, background: cream, borderRadius: 10, padding: '16px 18px' }}>
                      <div style={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: sub, marginBottom: 10, fontWeight: 700 }}>{w} Wing</div>
                      {[['Capacity', ws.length, sub],['Allocated', wa, navy],['Vacant', ws.length-wa, green],['Occupancy', pct(wa,ws.length)+'%', sub]].map(([l,v,c]) => (
                        <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
                          <span>{l}</span><b style={{ fontFamily: 'monospace', color: c }}>{v}</b>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Room type bars */}
            <div style={{ background: white, borderRadius: 12, padding: '24px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 17, fontWeight: 600, color: navy, marginBottom: 6 }}>Occupancy by Room Type</div>
              <div style={{ fontSize: 11.5, color: sub, marginBottom: 18 }}>Share of beds currently allocated</div>
              {TYPE_ORDER.filter(t => beds.some(b => b.room_type === t)).map(t => {
                const ts = beds.filter(b => b.room_type === t);
                const ta = ts.filter(b => b.status === 'Allocated' || b.status === 'ALLOCATED').length;
                return <HBar key={t} label={TYPE_SHORT[t]||t} a={ta} t={ts.length} color={TYPE_COLOR[t]||navy} swatch/>;
              })}
              {/* Also handle room types not in TYPE_ORDER */}
              {[...new Set(beds.map(b=>b.room_type))].filter(t=>!TYPE_ORDER.includes(t)&&t).map(t => {
                const ts = beds.filter(b => b.room_type === t);
                const ta = ts.filter(b => b.status === 'Allocated' || b.status === 'ALLOCATED').length;
                return <HBar key={t} label={TYPE_SHORT[t]||t} a={ta} t={ts.length} color={TYPE_COLOR[t]||navy} swatch/>;
              })}
            </div>
          </div>

          {/* Floor matrix */}
          <div style={{ background: white, borderRadius: 12, padding: '24px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 17, fontWeight: 600, color: navy, marginBottom: 4 }}>Floor-wise Occupancy Matrix</div>
            <div style={{ fontSize: 11.5, color: sub, marginBottom: 18 }}>Each cell shows allocated / total beds. Click a cell to open in Bed Register.</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...th0, textAlign: 'left' }}>Floor</th>
                    {['A','B'].map(t => (
                      <React.Fragment key={t}>
                        <th style={{ ...th0, textAlign: 'center' }}>Tower {t} · Front</th>
                        <th style={{ ...th0, textAlign: 'center' }}>Tower {t} · Rear</th>
                        <th style={{ ...th0, textAlign: 'center' }}>Tower {t} · Total</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...stats.floors].reverse().map(f => (
                    <tr key={f}>
                      <td style={{ ...td0, fontWeight: 700, color: navy, whiteSpace: 'nowrap' }}>Floor {f}</td>
                      {['A','B'].map(t => (
                        <React.Fragment key={t}>
                          {['Front','Rear',null].map(w => {
                            const set = beds.filter(b => b.tower===t && b.floor===f && (w ? b.wing===w : true));
                            const a   = set.filter(b => b.status==='Allocated'||b.status==='ALLOCATED').length;
                            const p   = pct(a, set.length);
                            return (
                              <td key={w||'total'} style={{ ...td0, textAlign: 'center' }}>
                                {set.length > 0 ? (
                                  <div onClick={() => jumpTo(t, f, w||'')}
                                    style={{ borderRadius: 6, padding: '8px 4px', fontSize: 11, fontWeight: 700,
                                      color: white, background: occColor(p), cursor: 'pointer', minWidth: 54 }}>
                                    {a} / {set.length}
                                    <small style={{ display: 'block', fontWeight: 500, fontSize: 9.5, opacity: 0.85, marginTop: 1 }}>{p}%</small>
                                  </div>
                                ) : <span style={{ color: light }}>—</span>}
                              </td>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr style={{ borderTop: `2px solid ${light}` }}>
                    <td style={{ ...td0, fontWeight: 700, color: navy }}>All Floors</td>
                    {['A','B'].map(t => (
                      <React.Fragment key={t}>
                        {['Front','Rear',null].map(w => {
                          const set = beds.filter(b => b.tower===t && (w ? b.wing===w : true));
                          const a   = set.filter(b => b.status==='Allocated'||b.status==='ALLOCATED').length;
                          return (
                            <td key={w||'total'} style={{ ...td0, textAlign: 'center' }}>
                              <div style={{ borderRadius: 6, padding: '8px 4px', fontSize: 11, fontWeight: 700, color: white, background: deep, minWidth: 54 }}>
                                {a} / {set.length}
                                <small style={{ display: 'block', fontWeight: 500, fontSize: 9.5, opacity: 0.85, marginTop: 1 }}>{pct(a,set.length)}%</small>
                              </div>
                            </td>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ BY ROOM TYPE TAB ═══════════════════════════════════════════════════ */}
      {tab === 'types' && (
        <div>
          <div style={{ background: white, borderRadius: 12, padding: '24px 32px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 24, fontWeight: 500, color: navy, marginBottom: 4 }}>Allocation by Room Type</h2>
              <p style={{ fontSize: 13, color: sub }}>Bed capacity, allocations and vacancies for each accommodation category</p>
            </div>
            <select style={sel} value={typeTower} onChange={e => setTypeTower(e.target.value)}>
              <option value="">Both Towers</option>
              <option value="A">Tower A</option>
              <option value="B">Tower B</option>
            </select>
          </div>
          <div style={{ background: white, borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${light}` }}>
              <span style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 18, fontWeight: 600, color: navy }}>Room Type Summary</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: cream }}>
                  {['Room Type','Rooms','Total Beds','Allocated','Vacant','Occupancy'].map(h => (
                    <th key={h} style={{ ...th0, textAlign: h==='Room Type'?'left':'right' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const data = typeTower ? beds.filter(b => b.tower === typeTower) : beds;
                  const allTypes = [...new Set(data.map(b=>b.room_type).filter(Boolean))];
                  const orderedTypes = [...TYPE_ORDER.filter(t => allTypes.includes(t)), ...allTypes.filter(t => !TYPE_ORDER.includes(t))];
                  const rows = orderedTypes.map(t => {
                    const set   = data.filter(b => b.room_type === t);
                    const rooms = new Set(set.map(b => b.room)).size;
                    const a     = set.filter(b => b.status==='Allocated'||b.status==='ALLOCATED').length;
                    return { t, rooms, beds: set.length, a, v: set.length - a };
                  });
                  const tBeds  = data.length;
                  const tAlloc = data.filter(b => b.status==='Allocated'||b.status==='ALLOCATED').length;
                  const tRooms = new Set(data.map(b=>b.room)).size;
                  return (
                    <>
                      {rows.map(r => {
                        const p = pct(r.a, r.beds);
                        const c = TYPE_COLOR[r.t] || navy;
                        return (
                          <tr key={r.t}>
                            <td style={td0}><TypePill type={r.t}/></td>
                            <td style={{ ...td0, textAlign: 'right', fontFamily: 'monospace' }}>{r.rooms}</td>
                            <td style={{ ...td0, textAlign: 'right', fontFamily: 'monospace' }}>{r.beds}</td>
                            <td style={{ ...td0, textAlign: 'right', fontFamily: 'monospace', color: navy, fontWeight: 700 }}>{r.a}</td>
                            <td style={{ ...td0, textAlign: 'right', fontFamily: 'monospace', color: green, fontWeight: 700 }}>{r.v}</td>
                            <td style={{ ...td0, minWidth: 180 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1, height: 8, background: light, borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{ width: `${p}%`, height: '100%', background: c, borderRadius: 4 }}/>
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: c, width: 36, textAlign: 'right' }}>{p}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ background: 'rgba(27,59,95,0.04)', fontWeight: 700 }}>
                        <td style={{ ...td0, fontWeight: 700, color: navy }}>TOTAL</td>
                        <td style={{ ...td0, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{tRooms}</td>
                        <td style={{ ...td0, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{tBeds}</td>
                        <td style={{ ...td0, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: navy }}>{tAlloc}</td>
                        <td style={{ ...td0, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: green }}>{tBeds - tAlloc}</td>
                        <td style={{ ...td0, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{pct(tAlloc,tBeds)}%</td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ BED REGISTER TAB ═══════════════════════════════════════════════════ */}
      {tab === 'register' && (
        <div>
          <div style={{ background: white, borderRadius: 12, padding: '24px 32px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24 }}>
            <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 24, fontWeight: 500, color: navy, marginBottom: 4 }}>Bed Register</h2>
            <p style={{ fontSize: 13, color: sub }}>Bed-level allocation status across both towers — filter, search and export</p>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 22 }}>
            <span style={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: sub, fontWeight: 600 }}>Filter by:</span>
            <select style={sel} value={fTower} onChange={e=>{setFTower(e.target.value);setPage(1);}}>
              <option value="">All Towers</option><option value="A">Tower A</option><option value="B">Tower B</option>
            </select>
            <select style={sel} value={fFloor} onChange={e=>{setFFloor(e.target.value);setPage(1);}}>
              <option value="">All Floors</option>
              {stats.floors.map(f => <option key={f} value={f}>Floor {f}</option>)}
            </select>
            <select style={sel} value={fWing} onChange={e=>{setFWing(e.target.value);setPage(1);}}>
              <option value="">All Wings</option><option value="Front">Front</option><option value="Rear">Rear</option>
            </select>
            <select style={sel} value={fType} onChange={e=>{setFType(e.target.value);setPage(1);}}>
              <option value="">All Types</option>
              {[...new Set(beds.map(b=>b.room_type).filter(Boolean))].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select style={sel} value={fStatus} onChange={e=>{setFStatus(e.target.value);setPage(1);}}>
              <option value="">All Status</option><option value="Allocated">Allocated</option><option value="Vacant">Vacant</option>
            </select>
            <button onClick={()=>{setFTower('');setFFloor('');setFWing('');setFType('');setFStatus('');setFQ('');setPage(1);}}
              style={{ padding:'8px 14px', background:'transparent', border:`1.5px solid ${light}`, borderRadius:6,
                cursor:'pointer', fontSize:11, fontWeight:600, color:navy, fontFamily:'Work Sans,sans-serif', marginLeft:'auto' }}>
              ✕ Clear
            </button>
          </div>

          <div style={{ background: white, borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${light}`, gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 18, fontWeight: 600, color: navy }}>
                {regFiltered.length} Beds · {regFiltered.filter(b=>b.status==='Allocated'||b.status==='ALLOCATED').length} allocated · {regFiltered.filter(b=>b.status!=='Allocated'&&b.status!=='ALLOCATED').length} vacant
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: cream, borderRadius: 8, padding: '8px 14px', border: `1.5px solid transparent` }}>
                <span>🔍</span>
                <input type="text" value={fQ} onChange={e=>{setFQ(e.target.value);setPage(1);}} placeholder="Search bed, room or student…"
                  style={{ border:'none', background:'transparent', fontFamily:'Work Sans,sans-serif', fontSize:13, color:'#2C2C2C', outline:'none', width:200 }}/>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: cream }}>
                  {['Bed ID','Tower','Floor','Wing','Room','Room Type','Status','Occupant'].map(h => (
                    <th key={h} style={th0}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {regPage.length > 0 ? regPage.map((b, i) => (
                  <tr key={i}>
                    <td style={{ ...td0, fontFamily: 'monospace', fontSize: 12, color: sub }}>{b.bed_id}</td>
                    <td style={td0}>
                      <span style={{ display:'inline-flex', width:22, height:22, borderRadius:5, alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:white, background: TOWER_COLOR[b.tower]||navy }}>
                        {b.tower}
                      </span>
                    </td>
                    <td style={td0}>Floor {b.floor}</td>
                    <td style={td0}>{b.wing}</td>
                    <td style={{ ...td0, fontFamily: 'monospace', fontSize: 12, color: sub }}>{b.room}</td>
                    <td style={td0}><TypePill type={b.room_type}/></td>
                    <td style={td0}><StatusPill status={b.status}/></td>
                    <td style={td0}>
                      {(b.student||b.studentName)
                        ? <span style={{ fontWeight: 600, color: navy }}>{b.student||b.studentName}</span>
                        : <span style={{ color: light }}>—</span>}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={8} style={{ textAlign:'center', color:sub, padding:30 }}>No beds match these filters.</td></tr>
                )}
              </tbody>
            </table>

            <div style={{ padding:'13px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', borderTop:`1px solid ${light}`, background:cream }}>
              <span style={{ fontSize:12, color:sub }}>
                Showing {regFiltered.length ? (page-1)*PG+1 : 0}–{Math.min(page*PG, regFiltered.length)} of {regFiltered.length} beds
              </span>
              <div style={{ display:'flex', gap:4 }}>
                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                  style={{ width:32, height:32, borderRadius:6, border:`1.5px solid ${light}`, background:white, fontSize:12, fontWeight:600, cursor:'pointer', color:sub, opacity:page===1?0.35:1 }}>‹</button>
                {Array.from({length:Math.min(pages,5)},(_,i)=>i+1).map(p => (
                  <button key={p} onClick={()=>setPage(p)}
                    style={{ width:32, height:32, borderRadius:6, border:`1.5px solid ${p===page?navy:light}`,
                      background:p===page?navy:white, color:p===page?white:sub, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    {p}
                  </button>
                ))}
                <button onClick={()=>setPage(p=>Math.min(pages,p+1))} disabled={page>=pages}
                  style={{ width:32, height:32, borderRadius:6, border:`1.5px solid ${light}`, background:white, fontSize:12, fontWeight:600, cursor:'pointer', color:sub, opacity:page>=pages?0.35:1 }}>›</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}